import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface LegalSettings {
  id: string;
  company_name: string | null;
  company_type: string;
  owner_name: string | null;
  street: string | null;
  zip_code: string | null;
  city: string | null;
  country: string;
  email: string | null;
  phone: string | null;
  register_court: string | null;
  register_number: string | null;
  vat_id: string | null;
  responsible_person: string | null;
  data_protection_officer: string | null;
  data_protection_officer_email: string | null;
  privacy_content: string | null;
  terms_content: string | null;
  content_updated_at: string | null;
  // Versionen und Art.-9-Text (Migration 172). Die Version steht damit
  // neben dem Text, statt als Konstante im Frontend-Bundle zu haengen.
  privacy_version: string;
  terms_version: string;
  health_consent_version: string;
  health_consent_content: string | null;
}

export function useLegalSettings() {
  return useQuery({
    queryKey: ['legal-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as LegalSettings | null;
    },
    staleTime: 1000 * 60 * 60, // 1 hour cache
  });
}

/**
 * Replace placeholders in HTML content with actual legal settings values
 */
export function replacePlaceholders(content: string | null, settings: LegalSettings | null): string {
  if (!content || !settings) return content || '';

  const companyName = settings.company_name || settings.owner_name || 'ParentPath';
  const address = [
    settings.street,
    `${settings.zip_code || ''} ${settings.city || ''}`.trim(),
    settings.country !== 'Deutschland' ? settings.country : null
  ].filter(Boolean).join(', ');

  const replacements: Record<string, string> = {
    '{{company_name}}': companyName,
    '{{address}}': address,
    '{{street}}': settings.street || '',
    '{{zip_code}}': settings.zip_code || '',
    '{{city}}': settings.city || '',
    '{{country}}': settings.country || 'Deutschland',
    '{{email}}': settings.email || '[E-Mail nicht konfiguriert]',
    '{{phone}}': settings.phone || '',
    '{{register_court}}': settings.register_court || '',
    '{{register_number}}': settings.register_number || '',
    '{{vat_id}}': settings.vat_id || '',
    '{{responsible_person}}': settings.responsible_person || '',
    '{{data_protection_officer}}': settings.data_protection_officer || '',
    '{{dpo_email}}': settings.data_protection_officer_email || settings.email || '',
    '{{owner_name}}': settings.owner_name || '',
  };

  let result = content;
  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
  }

  // Handle conditional blocks {{#if data_protection_officer}}...{{/if}}
  result = result.replace(
    /\{\{#if data_protection_officer\}\}([\s\S]*?)\{\{\/if\}\}/g,
    settings.data_protection_officer ? '$1' : ''
  );

  return result;
}
